export interface ClientImportRow {
  rowId: string;
  name: string;
  phone: string;
  email?: string;
  birthday?: string;
  preferredStyle?: string;
  serviceFormula?: string;
  privateNotes?: string;
}

const aliases: Record<string, keyof Omit<ClientImportRow, "rowId">> = {
  name: "name",
  성명: "name",
  회원명: "name",
  이름: "name",
  customername: "name",
  고객명: "name",
  phone: "phone",
  hp: "phone",
  휴대폰: "phone",
  휴대폰번호: "phone",
  핸드폰: "phone",
  휴대전화: "phone",
  연락처: "phone",
  전화번호: "phone",
  mobile: "phone",
  email: "email",
  이메일: "email",
  birthday: "birthday",
  생일: "birthday",
  생년월일: "birthday",
  preferredstyle: "preferredStyle",
  "선호 스타일": "preferredStyle",
  선호스타일: "preferredStyle",
  serviceformula: "serviceFormula",
  "시술식/약제": "serviceFormula",
  시술식: "serviceFormula",
  privatememo: "privateNotes",
  privatenotes: "privateNotes",
  "비공개 메모": "privateNotes",
  메모: "privateNotes",
  특이사항: "privateNotes",
  고객메모: "privateNotes",
};

export function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]!;
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === "," && !quoted) {
      row.push(cell.trim());
      cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += character;
    }
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);
  if (quoted) throw new Error("csv_quote_unclosed");
  return rows;
}

export function csvHeaderKey(value: string) {
  return value
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replaceAll("_", "")
    .replaceAll(" ", "");
}

export type ClientColumn = keyof Omit<ClientImportRow, "rowId">;
export function clientCsvMapping(header: string[]) { return header.map(value => aliases[csvHeaderKey(value)] || ""); }
export function parseClientCsv(text: string, columns?: Array<ClientColumn | "">): ClientImportRow[] {
  const rows = parseCsvRows(text);
  const header = rows.shift();
  if (!header) throw new Error("csv_empty");
  const mapping = columns || clientCsvMapping(header);
  if (!mapping.includes("name") || !mapping.includes("phone"))
    throw new Error("csv_headers_required");
  if (rows.length > 500) throw new Error("csv_too_many_rows");
  return rows.map((cells, rowIndex) => {
    const record: ClientImportRow = {
      rowId: String(rowIndex + 2),
      name: "",
      phone: "",
    };
    mapping.forEach((key, columnIndex) => {
      if (key) record[key] = cells[columnIndex] || "";
    });
    const carried = header.flatMap((name, i) => ["最近訪問日", "최근방문일", "방문횟수", "누적금액"].includes(name.trim()) && cells[i] ? [`${name}: ${cells[i]}`] : []);
    if (carried.length) record.privateNotes = [record.privateNotes, `가져온 기록: ${carried.join(" · ")}`].filter(Boolean).join("\n");
    if (record.birthday) { const digits = record.birthday.replace(/\D/g, ""); if (digits.length === 8) record.birthday = `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}`; }
    return record;
  });
}

export async function readClientCsv(file: File): Promise<{ text: string; encoding: "UTF-8" | "CP949" }> {
  if (/\.xlsx?$/i.test(file.name)) throw new Error("csv_required");
  const bytes = await file.arrayBuffer();
  const utf = new TextDecoder("utf-8").decode(bytes);
  return utf.includes("\uFFFD") ? { text: new TextDecoder("euc-kr").decode(bytes), encoding: "CP949" } : { text: utf, encoding: "UTF-8" };
}
// Same-tab memory only. No customer file or raw record is stored on disk or in analytics.
let pendingRows: ClientImportRow[] = [];
export const switchRows = { get: () => pendingRows, set: (rows: ClientImportRow[]) => { pendingRows = rows; }, clear: () => { pendingRows = []; } };
